import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { sitesApi } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";
import { TrustPill } from "../components/TrustPill";
import { ExternalSiteAddModal } from "../components/ExternalSiteAddModal";

export function SitesPage() {
  const q = useQuery({ queryKey: ["sites"], queryFn: sitesApi.list, refetchInterval: 60_000 });
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<"all" | "internal_lead_gen" | "external_managed">("all");
  const [addModalOpen, setAddModalOpen] = useState(false);
  type SiteRow = NonNullable<typeof q.data>["sites"][number];
  const filteredSites = (q.data?.sites ?? []).filter((s) => {
    if (kindFilter === "all") return true;
    return (s.siteKind ?? "internal_lead_gen") === kindFilter;
  });
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<SiteRow>(
    filteredSites,
    {
      domain: (s) => s.domain,
      market: (s) => [s.city, s.state].filter(Boolean).join(", "),
      author: (s) => s.authorName,
      phone: (s) => s.phoneNumber,
      posts: (s) => s.blogPostCount,
      mode: (s) => s.mode,
      status: (s) => s.status,
      audit: (s) => s.latestAudit ? (s.latestAudit.errorCount > 0 ? 2 : s.latestAudit.warnCount > 0 ? 1 : 0) : null,
      indexed: (s) => s.totalUrlsChecked > 0 ? s.indexedUrls / s.totalUrlsChecked : null,
      ranked: (s) => s.rankedKeywords,
      recent: (s) => s.recentLeads,
      highScore: (s) => s.highScoreLeads,
      totalLeads: (s) => s.totalLeads,
    },
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Sites</h1>
          <p className="text-ink-600 text-sm mt-1">Every domain in the portfolio.</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setAddModalOpen(true)}
            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1"
            title="Add an external_managed site (one you own + RankForge tracks but doesn't deploy)"
          >+ External site</button>
          <div className="flex gap-1 text-sm">
            {(["all", "internal_lead_gen", "external_managed"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setKindFilter(k)}
                className={`px-3 py-1 rounded border text-xs ${kindFilter === k ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-ink-200 hover:bg-ink-50"}`}
              >
                {k === "all" ? "All" : k === "internal_lead_gen" ? "Internal (lead-gen)" : "External (managed)"}
              </button>
            ))}
          </div>
          {q.data && (
            <div className="text-right">
              <div className="text-2xl font-display font-bold">{filteredSites.length}</div>
              <div className="text-xs uppercase tracking-wider text-ink-500">{kindFilter === "all" ? "total" : "filtered"}</div>
            </div>
          )}
        </div>
      </div>
      <ExternalSiteAddModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />

      {q.isLoading && <Loading />}
      {q.error && <ErrorBox msg={String((q.error as Error).message)} />}
      {q.data && (
        q.data.sites.length === 0 ? <EmptyState /> : (
          <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
                <tr>
                  <SortHeader label="Domain" sortKey="domain" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Market" sortKey="market" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Author" sortKey="author" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" title="Per-site editorial author. (fallback) means the site uses operator.yaml's author." />
                  <SortHeader label="📞 Phone" sortKey="phone" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" title="Per-site Twilio/SignalWire tracking number. ✓ = provisioned (default mode is voicemail). — = not provisioned. Run `rankforge twilio provision --domain X --no-dry-run` to add." />
                  <SortHeader label="Posts" sortKey="posts" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" title="Long-form blog posts in blog_posts. 0 = site renders the 'Articles coming soon' stub. Run `rankforge blog-write-bulk` to seed." />
                  <SortHeader label="Mode" sortKey="mode" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Status" sortKey="status" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Audit" sortKey="audit" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Indexed URLs" sortKey="indexed" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" title="URLs reported as 'indexed' by Google Search Console URL Inspection (run via `rankforge index-check`)" />
                  <th className="text-right px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      Ranked KWs <TrustPill label={q.data?.dataIntegrity?.rankedKeywords} />
                    </span>
                    <div className="font-normal normal-case tracking-normal text-[10px] text-ink-500 mt-0.5">desktop · mobile · gsc</div>
                  </th>
                  <th className="text-right px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      Leads (7d) <TrustPill label={q.data?.dataIntegrity?.recentLeads} />
                    </span>
                  </th>
                  <th className="text-right px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      High score <TrustPill label={q.data?.dataIntegrity?.highScoreLeads} />
                    </span>
                  </th>
                  <th className="text-right px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      Total leads <TrustPill label={q.data?.dataIntegrity?.totalLeads} />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {sortedRows.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/sites/${s.id}`)}
                    className="cursor-pointer hover:bg-ink-50/50"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/sites/${s.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-ink-900 hover:text-accent no-underline"
                      >
                        {s.domain}
                      </Link>
                      {s.siteKind === "external_managed" && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-purple-100 text-purple-800 border border-purple-200" title="External-managed site — RankForge tracks rank + pushes content; you own deploy">EXT</span>
                      )}
                      <a
                        href={`https://${s.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="ml-2 text-xs text-ink-400 hover:text-ink-700"
                        aria-label="Open live site"
                      >↗</a>
                    </td>
                    <td className="px-4 py-3 text-ink-600">
                      {[s.city, s.state].filter(Boolean).join(", ") || "—"} · {s.niche || "—"}
                    </td>
                    <td className="px-4 py-3 text-ink-600 text-xs">
                      {s.authorName ?? <span className="text-ink-400 italic">(fallback)</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {s.phoneNumber ? (
                        <div className="flex flex-col leading-tight">
                          <span className="font-mono text-ink-700">{formatUsPhone(s.phoneNumber)}</span>
                          <span className={`text-[10px] uppercase tracking-wide ${s.callMode === "voicemail" ? "text-purple-700" : s.callMode === "forward" ? "text-green-700" : "text-ink-500"}`}>
                            {s.callMode ?? "?"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {s.blogPostCount > 0 ? <strong>{s.blogPostCount}</strong> : <span className="text-ink-400">0</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={s.mode === "branded_lessee" ? "amber" : "blue"}>
                        {s.mode === "branded_lessee" ? "Branded" : "Lead-gen"}
                      </Pill>
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={statusTone(s.status)}>{s.status}</Pill>
                    </td>
                    <td className="px-4 py-3">
                      <AuditCell audit={s.latestAudit} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <IndexedCell indexed={s.indexedUrls} total={s.totalUrlsChecked} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {s.rankings ? (
                        <div className="flex flex-col items-end leading-tight">
                          <span className="font-semibold text-ink-900">
                            {s.rankings.desktop} · {s.rankings.mobile} · {s.rankings.gscQueriesSeen}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide text-ink-500">d · m · gsc</span>
                        </div>
                      ) : s.rankedKeywords > 0 ? (
                        <strong>{s.rankedKeywords}</strong>
                      ) : (
                        <span className="text-ink-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {s.recentLeads > 0 ? <strong>{s.recentLeads}</strong> : <span className="text-ink-400">0</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {s.highScoreLeads > 0 ? s.highScoreLeads : <span className="text-ink-400">0</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.totalLeads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )
      )}

      {q.data && q.data.sites.length > 0 && (
        <p className="text-xs text-ink-500 mt-3">
          <Link to="/leads" className="underline decoration-dotted hover:text-ink-900">View lead inbox →</Link>
        </p>
      )}
    </div>
  );
}

function formatUsPhone(e164: string): string {
  // E.164 → "(XXX) XXX-XXXX" for readability. Best-effort: returns
  // the raw value if it doesn't match the US 11-digit pattern.
  const m = /^\+1?(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

function IndexedCell({ indexed, total }: { indexed: number; total: number }) {
  if (total === 0) return <span className="text-ink-400" title="No URL Inspection data — run `rankforge index-check`">—</span>;
  const pct = Math.round((indexed / total) * 100);
  const tone = pct >= 80 ? "text-green-700" : pct >= 50 ? "text-amber-700" : "text-red-700";
  return (
    <span title={`${indexed} of ${total} URLs indexed`}>
      <strong className={tone}>{indexed}</strong>
      <span className="text-ink-400 text-xs">/{total}</span>
    </span>
  );
}

function AuditCell({ audit }: { audit: import("../lib/api").AuditSummary | null }) {
  if (!audit) return <span className="text-xs text-ink-400">never run</span>;
  const tone = audit.errorCount > 0 ? "red" : audit.warnCount > 0 ? "amber" : "green";
  const colors: Record<string, string> = {
    green: "bg-green-100 text-green-800",
    amber: "bg-accent-100 text-accent-700",
    red: "bg-red-100 text-red-800",
  };
  const label = audit.errorCount > 0
    ? `${audit.errorCount} fail`
    : audit.warnCount > 0
      ? `${audit.warnCount} warn`
      : "clean";
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-medium ${colors[tone]}`}>{label}</span>
      <span className="text-[11px] text-ink-500">{relTime(audit.ranAt)}</span>
    </div>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function Pill({ children, tone }: { children: React.ReactNode; tone: "blue" | "amber" | "green" | "gray" }) {
  const colors: Record<string, string> = {
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-accent-100 text-accent-700",
    green: "bg-green-100 text-green-800",
    gray: "bg-ink-100 text-ink-700",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-medium ${colors[tone]}`}>
      {children}
    </span>
  );
}

function statusTone(status: string): "blue" | "amber" | "green" | "gray" {
  if (status === "live") return "green";
  if (status === "rented") return "amber";
  if (status === "deploying") return "blue";
  return "gray";
}

function Loading() {
  return <div className="bg-white rounded-xl border border-ink-100 p-12 text-center text-ink-500">Loading…</div>;
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
      <strong>Failed to load sites.</strong> {msg}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-12 text-center">
      <p className="text-ink-700 font-medium">No sites yet.</p>
      <p className="text-ink-500 text-sm mt-1">
        Run <code className="bg-ink-100 px-1.5 py-0.5 rounded text-xs">rankforge generate</code> to add one.
      </p>
    </div>
  );
}
